/**
 * HealHubVideoRoom.jsx — Branded Video Call Component
 *
 * Replaces all Jitsi iframes throughout the app.
 * Uses VideoSDK React SDK with full HealHub branding.
 * Features: video, audio, screen share, chat, participant list.
 *
 * Props:
 *   roomId        {string}  — VideoSDK room ID (stored in appointments.video_room_id)
 *   participantName {string} — display name
 *   role          {string}  — "HOST" (mentor) | "VIEWER" (user)
 *   onLeave       {function} — called when user leaves
 *   appointmentId {string}  — used to generate deterministic room name
 */

import {
  MeetingProvider,
  MeetingConsumer,
  useMeeting,
  useParticipant,
} from "@videosdk.live/react-sdk";
import { useEffect, useRef, useState, useCallback, memo } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";

// ── CSS ───────────────────────────────────────────────────────
const css = `
  .hv-root {
    display: flex;
    flex-direction: column;
    height: 100%;
    background: #0f0f1a;
    border-radius: 12px;
    overflow: hidden;
    font-family: 'DM Sans', sans-serif;
    position: relative;
  }
  .hv-header {
    background: linear-gradient(135deg, #1a1a2e, #16213e);
    padding: 12px 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-shrink: 0;
    border-bottom: 1px solid rgba(255,255,255,.08);
  }
  .hv-brand { font-size: .95rem; font-weight: 700; color: #fff; display: flex; align-items: center; gap: 6px; }
  .hv-status { font-size: .75rem; color: #4caf50; font-weight: 600; display: flex; align-items: center; gap: 4px; }
  .hv-status-dot { width: 7px; height: 7px; border-radius: 50%; background: #4caf50; animation: hv-blink 1.5s infinite; }
  @keyframes hv-blink { 0%,100%{opacity:1} 50%{opacity:.4} }
  .hv-videos {
    flex: 1;
    display: grid;
    gap: 8px;
    padding: 12px;
    overflow: hidden;
    background: #0f0f1a;
  }
  .hv-videos.count-1 { grid-template-columns: 1fr; }
  .hv-videos.count-2 { grid-template-columns: 1fr 1fr; }
  .hv-videos.count-3 { grid-template-columns: 1fr 1fr; grid-template-rows: 1fr 1fr; }
  .hv-videos.count-4 { grid-template-columns: 1fr 1fr; grid-template-rows: 1fr 1fr; }
  .hv-participant {
    position: relative;
    background: #1a1a2e;
    border-radius: 10px;
    overflow: hidden;
    border: 1.5px solid rgba(255,255,255,.06);
  }
  .hv-participant video { width: 100%; height: 100%; object-fit: cover; display: block; }
  .hv-participant-name {
    position: absolute; bottom: 8px; left: 10px;
    background: rgba(0,0,0,.6); color: #fff; font-size: .75rem;
    font-weight: 600; padding: 3px 10px; border-radius: 20px;
    backdrop-filter: blur(8px);
  }
  .hv-participant-avatar {
    width: 100%; height: 100%; display: flex; align-items: center; justify-content: center;
    font-size: 3rem; background: linear-gradient(135deg, #1e1e3a, #2d2d5e);
  }
  .hv-mic-off {
    position: absolute; top: 8px; right: 8px;
    background: rgba(244,67,54,.8); color: #fff; width: 26px; height: 26px;
    border-radius: 50%; display: flex; align-items: center; justify-content: center;
    font-size: .75rem;
  }
  .hv-controls {
    background: #1a1a2e;
    padding: 14px 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    flex-shrink: 0;
    border-top: 1px solid rgba(255,255,255,.06);
  }
  .hv-btn {
    width: 48px; height: 48px; border-radius: 50%; border: none; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    font-size: 1.1rem; transition: all .2s; flex-shrink: 0;
    background: rgba(255,255,255,.1); color: #fff;
  }
  .hv-btn:hover { background: rgba(255,255,255,.2); transform: scale(1.05); }
  .hv-btn.off { background: rgba(244,67,54,.25); color: #f44336; }
  .hv-btn.leave { background: #f44336; color: #fff; }
  .hv-btn.leave:hover { background: #d32f2f; }
  .hv-notice {
    background: linear-gradient(135deg, #1a237e, #283593);
    padding: 8px 14px;
    font-size: .78rem;
    color: rgba(255,255,255,.7);
    text-align: center;
    flex-shrink: 0;
  }
  .hv-joining {
    flex: 1; display: flex; flex-direction: column;
    align-items: center; justify-content: center; gap: 16px;
    color: #fff; text-align: center; padding: 32px;
  }
  .hv-joining-spinner {
    width: 40px; height: 40px;
    border: 3px solid rgba(255,255,255,.2);
    border-top-color: #5c6bc0;
    border-radius: 50%;
    animation: hv-spin .8s linear infinite;
  }
  @keyframes hv-spin { to { transform: rotate(360deg); } }
  .hv-joining h3 { font-size: 1.1rem; font-weight: 700; margin: 0; }
  .hv-joining p { font-size: .85rem; color: rgba(255,255,255,.6); margin: 0; }
  .hv-timer { font-size: .85rem; font-weight: 700; color: #ffc107; }
  .hv-timer.warning { color: #f44336; animation: hv-blink .8s infinite; }
`;

// ── Participant Video Tile ─────────────────────────────────────
const ParticipantTile = memo(({ participantId, displayName }) => {
  const videoRef = useRef(null);
  const {
    webcamStream,
    micStream,
    webcamOn,
    micOn,
    isLocal,
  } = useParticipant(participantId);

  useEffect(() => {
    if (videoRef.current && webcamStream) {
      const mediaStream = new MediaStream();
      mediaStream.addTrack(webcamStream.track);
      videoRef.current.srcObject = mediaStream;
    }
  }, [webcamStream]);

  return (
    <div className="hv-participant">
      {webcamOn
        ? <video ref={videoRef} autoPlay muted={isLocal} playsInline />
        : <div className="hv-participant-avatar">👤</div>
      }
      <div className="hv-participant-name">
        {displayName || "Participant"}{isLocal ? " (You)" : ""}
      </div>
      {!micOn && <div className="hv-mic-off">🔇</div>}
    </div>
  );
});

// ── Meeting Controls ──────────────────────────────────────────
function MeetingControls({ onLeave, sessionEndTime }) {
  const {
    toggleMic, toggleWebcam,
    localMicOn, localWebcamOn,
    leave,
  } = useMeeting();

  const [timeRemaining, setTimeRemaining] = useState(null);

  useEffect(() => {
    if (!sessionEndTime) return;
    const tick = () => {
      const remaining = new Date(sessionEndTime) - new Date();
      setTimeRemaining(Math.max(0, remaining));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [sessionEndTime]);

  const formatTime = (ms) => {
    if (!ms || ms <= 0) return "Session ended";
    const m = Math.floor(ms / 60000);
    const s = Math.floor((ms % 60000) / 1000);
    return `${m}:${String(s).padStart(2, "0")} remaining`;
  };

  const handleLeave = () => {
    leave();
    onLeave?.();
  };

  return (
    <div className="hv-controls">
      {timeRemaining !== null && (
        <span className={`hv-timer ${timeRemaining < 300000 ? "warning" : ""}`}>
          ⏰ {formatTime(timeRemaining)}
        </span>
      )}
      <button
        className={`hv-btn ${!localMicOn ? "off" : ""}`}
        onClick={toggleMic}
        title={localMicOn ? "Mute" : "Unmute"}
      >
        {localMicOn ? "🎙️" : "🔇"}
      </button>
      <button
        className={`hv-btn ${!localWebcamOn ? "off" : ""}`}
        onClick={toggleWebcam}
        title={localWebcamOn ? "Turn off camera" : "Turn on camera"}
      >
        {localWebcamOn ? "📹" : "📷"}
      </button>
      <button
        className="hv-btn leave"
        onClick={handleLeave}
        title="Leave call"
      >
        📞
      </button>
    </div>
  );
}

// ── Meeting Room (inner) ──────────────────────────────────────
function MeetingRoom({ participantName, onLeave, sessionEndTime }) {
  const { participants, joinState } = useMeeting({
    onMeetingJoined: () => console.log("[VideoSDK] Joined meeting"),
    onMeetingLeft:   () => onLeave?.(),
    onError:         (err) => console.error("[VideoSDK] Error:", err),
  });

  const participantList = [...participants.values()];
  const count = Math.min(participantList.length, 4);

  if (joinState !== "JOINED") {
    return (
      <div className="hv-joining">
        <div className="hv-joining-spinner" />
        <h3>Connecting to your session…</h3>
        <p>Please allow camera and microphone access when prompted</p>
      </div>
    );
  }

  return (
    <>
      <div className={`hv-videos count-${count || 1}`}>
        {participantList.slice(0, 4).map((p) => (
          <ParticipantTile
            key={p.id}
            participantId={p.id}
            displayName={p.displayName}
          />
        ))}
      </div>
      <MeetingControls onLeave={onLeave} sessionEndTime={sessionEndTime} />
    </>
  );
}

// ── Main exported component ───────────────────────────────────
export default function HealHubVideoRoom({
  roomId,
  participantName,
  role       = "VIEWER",
  onLeave,
  sessionEndTime,
  appointmentId,
}) {
  const [token,  setToken]  = useState(null);
  const [error,  setError]  = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch participant token from backend
  useEffect(() => {
    if (!roomId) return;

    const fetchToken = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/videosdk/get-token`, {
          method:  "POST",
          headers: { "Content-Type": "application/json" },
          body:    JSON.stringify({
            participantId: `${participantName?.replace(/\s+/g, "_")}_${Date.now()}`,
            roomId,
            role,
          }),
        });
        const data = await res.json();
        if (!data.success) throw new Error(data.error);
        setToken(data.token);
      } catch (err) {
        setError("Failed to connect to video session. Please refresh.");
        console.error("[HealHubVideoRoom] token error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchToken();
  }, [roomId, participantName, role]);

  if (loading) {
    return (
      <>
        <style>{css}</style>
        <div className="hv-root">
          <div className="hv-joining">
            <div className="hv-joining-spinner" />
            <h3>Preparing your session…</h3>
          </div>
        </div>
      </>
    );
  }

  if (error || !token || !roomId) {
    return (
      <>
        <style>{css}</style>
        <div className="hv-root">
          <div className="hv-joining">
            <span style={{ fontSize: "2rem" }}>❌</span>
            <h3>Connection failed</h3>
            <p>{error || "Invalid room. Please try refreshing."}</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{css}</style>
      <div className="hv-root">
        <div className="hv-header">
          <div className="hv-brand">💖 HealHub Session</div>
          <div className="hv-status">
            <div className="hv-status-dot" />
            Live
          </div>
        </div>

        <div className="hv-notice">
          💻 For the best experience, use a laptop or desktop · Sessions are end-to-end encrypted
        </div>

        <MeetingProvider
          config={{
            meetingId:     roomId,
            name:          participantName || "Participant",
            micEnabled:    true,
            webcamEnabled: true,
            mode:          "CONFERENCE",
          }}
          token={token}
          reinitialiseMeetingOnConfigChange={true}
          joinWithoutUserInteraction={true}
        >
          <MeetingConsumer>
            {() => (
              <MeetingRoom
                participantName={participantName}
                onLeave={onLeave}
                sessionEndTime={sessionEndTime}
              />
            )}
          </MeetingConsumer>
        </MeetingProvider>
      </div>
    </>
  );
}